import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import {
  Appointment,
  AppointmentProps,
} from '../domain/appointment/appointment';

interface AppointmentSchema {
  date: Timestamp;
  duration?: string | null;
  comment?: string | null;
  location?: string | null;
  patientUserId?: string | null;
  origin: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp | null;
}

@Injectable()
export class AppointmentRepository {
  constructor(private firestore: AngularFirestore) {
    // Nada
  }

  private collection(patientId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection<AppointmentSchema>('patients')
      .doc(patientId)
      .collection('appointments', queryFn);
  }

  private toSchema(entity: Appointment): AppointmentSchema {
    return <AppointmentSchema>{
      date: Timestamp.fromDate(entity.date),
      duration: entity.duration?.toString() ?? null,
      comment: entity.comment ?? null,
      location: entity.location ?? null,
      patientUserId: entity.patientUserId ?? null,
      origin: entity.origin,
      createdAt: entity.createdAt
        ? Timestamp.fromDate(entity.createdAt)
        : Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  }

  private fromSchema(schema: AppointmentSchema, id?: string): Appointment {
    return Appointment.create(
      {
        date: schema.date.toDate(),
        duration: schema.duration?.toString() ?? undefined,
        comment: schema.comment ?? undefined,
        location: schema.location ?? undefined,
        patientUserId: schema.patientUserId ?? undefined,
        origin: schema.origin,
        createdAt: schema.createdAt.toDate(),
      } as AppointmentProps,
      new UniqueEntityID(id),
    );
  }

  async findAll(patientId: string): Promise<Appointment[]> {
    const snap = await firstValueFrom(
      this.collection(patientId, (qry) => qry.orderBy('date', 'asc')).get(),
    );
    return snap.docs.map((doc) =>
      this.fromSchema(doc.data() as AppointmentSchema, doc.id),
    );
  }

  async create(
    patientId: string,
    appointment: Appointment,
  ): Promise<Appointment> {
    const schema = this.toSchema(appointment);
    if (schema.duration && typeof schema.duration === 'number') {
      schema.duration = (schema.duration as number).toString();
    }
    const created = await this.collection(patientId).add(schema);
    return this.fromSchema(schema, created.id);
  }

  async update(
    patientId: string,
    appointment: Appointment,
  ): Promise<Appointment> {
    const schema = this.toSchema(appointment);
    await this.collection(patientId).doc(appointment.id.toString()).set(schema);
    return this.fromSchema(schema, appointment.id.toString());
  }

  async delete(patientId: string, appointmentId: string): Promise<void> {
    await this.collection(patientId).doc(appointmentId).delete();
  }
}
